import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { SuccessEffect, FailureEffect } from '../../effects';
import { Icon } from '../../foundation/primitives';
import { useMinigameSound } from '../../foundation/hooks/useMinigameSound';

/**
 * Template15: 選択の恐怖（DoorSelection）
 * 霊安室での扉選択 - 複数の扉から正解を探すホラーミニゲーム
 *
 * 正解かどうかは開けるまで分からない。扉は左端を軸に開き、
 * 開いた扉の中にズームインしていく演出。
 */
const Template15DoorSelection = ({
  hostSubmit,
  hostConfig,
  registryId = 'minigame_doorselection_15',
  templateId = 15
}) => {
  // T11〜T14 と同じ configKey パターン: hostConfig 未指定時に `?? {}` が毎レンダー新オブジェクトに
  // なっても、文字列キー比較で resolvedConfig の同一性を保ち、再レンダーループを防ぐ
  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const config = hostConfig ?? {};
    const safeNumber = (value, fallback) => (Number.isFinite(value) ? value : fallback);

    // デフォルトの10個の扉を生成
    const defaultDoors = Array.from({ length: 10 }, (_, i) => ({
      id: `door_${i + 1}`,
      label: `${i + 1}`,
      isCorrect: false,
      wrongBodyMessage: [
        '違う...これは見知らぬ老人だ...',
        'この人じゃない...若い女性の遺体...',
        '違う人だ...男性の遺体が横たわっている...',
        '子供...？違う、探している人じゃない...',
        'これは...別の誰かだ...',
        '冷たい遺体...でも違う...',
        '目を閉じた顔...知らない人だ...',
        'この人ではない...まだ探さなければ...'
      ][Math.floor(Math.random() * 8)]
    }));

    // configから渡された扉があればそれを使用、なければデフォルト
    let doors = Array.isArray(config.doors) && config.doors.length > 0
      ? config.doors
      : defaultDoors;

    // 正解の扉が設定されていない場合、ランダムに1つ設定
    if (!doors.some(d => d.isCorrect)) {
      const randomIndex = Math.floor(Math.random() * doors.length);
      doors = doors.map((d, i) => ({ ...d, isCorrect: i === randomIndex }));
    }

    return {
      title: config.title ?? '霊安室',
      instruction: config.instruction ?? '探している人を、見つけなければ...',
      doors,
      timeLimitSec: safeNumber(config.timeLimitSec, 0),
      showTimer: config.showTimer !== false,
      maxMistakes: safeNumber(config.maxMistakes, 9),
      heartbeatEnabled: config.heartbeatEnabled !== false,
      confirmBeforeOpen: config.confirmBeforeOpen !== false,
      successMessage: config.successMessage ?? '',
      failureMessage: config.failureMessage ?? 'もう...見つけられない...'
    };
  }, [configKey]);

  const backgroundStyle = useMinigameBackground(hostConfig);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const initializedRef = useRef(false);

  // R2 Wave2C: 心拍音をグローバル音エンジンへ寄せる（mood は manifest 通り dread）。
  // 旧来の「鳴らす度 new AudioContext()」はグローバルミュートを無視して音が漏れるため撤去。
  // 鼓動ハンドルは start/stop/setBpm を持ち、unlock 前・mute 中は鳴らない（hook が吸収）。
  const { heartbeat } = useMinigameSound('dread');
  const heartbeatHandleRef = useRef(null);

  const [gameState, setGameState] = useState('playing');
  const [selectedDoor, setSelectedDoor] = useState(null);
  const [pendingDoor, setPendingDoor] = useState(null);
  const [mistakes, setMistakes] = useState(0);
  const [timeLeft, setTimeLeft] = useState(resolvedConfig.timeLimitSec);
  const [showEffect, setShowEffect] = useState(null);
  const [triedDoors, setTriedDoors] = useState(new Set());
  const [currentWrongMessage, setCurrentWrongMessage] = useState('');
  const [heartbeatSpeed, setHeartbeatSpeed] = useState(1000);
  const [openingDoorIndex, setOpeningDoorIndex] = useState(null);
  const [zoomingIn, setZoomingIn] = useState(false);
  const [showDarkness, setShowDarkness] = useState(false);

  // 心臓の鼓動サウンド（グローバル音エンジン経由・mute 一元支配）。
  // 旧来は heartbeatSpeed[ms] で setInterval していたが、エンジンは bpm 駆動なので bpm = 60000 / speed に換算。
  // 1000ms → 60bpm（base）/ 300ms → 200bpm（最速）。ミス加速の挙動は handle.setBpm() で再現する。
  useEffect(() => {
    const shouldBeat =
      resolvedConfig.heartbeatEnabled && gameState !== 'success' && gameState !== 'fail';

    if (!shouldBeat) {
      // 成功/失敗/無効化のいずれかで停止（演出タイミングは旧来どおり「success/fail で止まる」）。
      if (heartbeatHandleRef.current) {
        heartbeatHandleRef.current.stop();
        heartbeatHandleRef.current = null;
      }
      return;
    }

    const bpm = 60000 / heartbeatSpeed;
    if (!heartbeatHandleRef.current) {
      heartbeatHandleRef.current = heartbeat(bpm);
    } else {
      heartbeatHandleRef.current.setBpm(bpm);
    }
    // アンマウントや依存変化での停止は次回の !shouldBeat 分岐／hook のアンマウント自動 stop が担う。
  }, [heartbeatSpeed, resolvedConfig.heartbeatEnabled, gameState, heartbeat]);

  // 心拍加速
  useEffect(() => {
    const baseSpeed = 1000;
    const minSpeed = 300;
    const speedDecrease = (baseSpeed - minSpeed) / resolvedConfig.maxMistakes;
    setHeartbeatSpeed(Math.max(minSpeed, baseSpeed - (mistakes * speedDecrease)));
  }, [mistakes, resolvedConfig.maxMistakes]);

  // タイマー
  useEffect(() => {
    if ((gameState !== 'playing' && gameState !== 'confirming') || resolvedConfig.timeLimitSec <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setGameState('fail');
          setShowEffect('fail');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState, resolvedConfig.timeLimitSec]);

  // 結果送信
  useEffect(() => {
    if (showEffect === 'success') {
      const timer = setTimeout(() => {
        submit('success', { selectedDoor: selectedDoor?.id, mistakes, timeRemaining: timeLeft });
      }, 2500);
      return () => clearTimeout(timer);
    }
    if (showEffect === 'fail') {
      const timer = setTimeout(() => {
        submit('fail', { selectedDoor: selectedDoor?.id, mistakes, timeRemaining: timeLeft });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showEffect, submit, selectedDoor, mistakes, timeLeft]);

  // 扉クリック
  const handleDoorClick = useCallback((door, index) => {
    if (gameState !== 'playing' || triedDoors.has(door.id)) return;

    if (resolvedConfig.confirmBeforeOpen) {
      setPendingDoor({ door, index });
      setGameState('confirming');
    } else {
      openDoor(door, index);
    }
  }, [gameState, triedDoors, resolvedConfig.confirmBeforeOpen]);

  const handleCancel = useCallback(() => {
    setPendingDoor(null);
    setGameState('playing');
  }, []);

  // 扉を開ける - 左端を軸に開き、ズームイン
  const openDoor = useCallback((door, index) => {
    setOpeningDoorIndex(index);
    setGameState('opening');
    setPendingDoor(null);

    // 扉が開くアニメーション
    setTimeout(() => {
      setZoomingIn(true);
    }, 600);

    // ズームイン完了後
    setTimeout(() => {
      setShowDarkness(true);
    }, 1200);

    // 結果表示
    setTimeout(() => {
      setOpeningDoorIndex(null);
      setZoomingIn(false);
      setShowDarkness(false);
      setSelectedDoor(door);
      setTriedDoors(prev => new Set([...prev, door.id]));

      if (door.isCorrect) {
        // 正解でも派手な演出はしない - 静かにフェードアウト
        setGameState('success');
        setShowEffect('success');
      } else {
        setCurrentWrongMessage(door.wrongBodyMessage || '違う人だ...');
        setGameState('wrong_body');
        const newMistakes = mistakes + 1;
        setMistakes(newMistakes);

        setTimeout(() => {
          if (resolvedConfig.maxMistakes > 0 && newMistakes >= resolvedConfig.maxMistakes) {
            setGameState('fail');
            setShowEffect('fail');
          } else {
            setGameState('playing');
          }
        }, 2500);
      }
    }, 1800);
  }, [mistakes, resolvedConfig.maxMistakes]);

  const handleConfirmOpen = useCallback(() => {
    if (pendingDoor) openDoor(pendingDoor.door, pendingDoor.index);
  }, [pendingDoor, openDoor]);

  // リセット（T11〜T14 と同じ initializedRef ガード: 初期化は初回マウント時のみ実行）
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setGameState('playing');
    setSelectedDoor(null);
    setPendingDoor(null);
    setMistakes(0);
    setTimeLeft(resolvedConfig.timeLimitSec);
    setTriedDoors(new Set());
    setCurrentWrongMessage('');
    setHeartbeatSpeed(1000);
    setOpeningDoorIndex(null);
    setZoomingIn(false);
    setShowDarkness(false);
    resetSubmission();
  }, [resolvedConfig, resetSubmission]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const remainingAttempts = resolvedConfig.maxMistakes - mistakes;
  const fearLevel = useMemo(() => Math.min(mistakes / resolvedConfig.maxMistakes, 0.7), [mistakes, resolvedConfig.maxMistakes]);

  return (
    <div
      className="t15-root relative w-full h-full flex flex-col items-center justify-center overflow-hidden"
      style={backgroundStyle ?? undefined}
    >
      {/* 暗闘オーバーレイ（fearLevel 駆動の暗幕アルファはデータ駆動のためインライン温存。
          ベースの黒は恐怖の没入で固定。transition-opacity duration-1000 は JSX に残す） */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-1000"
        style={{
          background: `radial-gradient(circle at center, transparent 20%, rgba(0,0,0,${0.3 + fearLevel * 0.5}) 100%)`,
          zIndex: 5
        }}
      />

      {/* ズームイン時の暗転（旧 bg-black。transition-opacity duration-500 と opacity インラインは JSX に残す） */}
      <div
        className="t15-darkness absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{ opacity: showDarkness ? 1 : 0, zIndex: 60 }}
      />

      {/* エフェクト */}
      <div style={{ zIndex: 70 }}>
        {showEffect === 'success' && <SuccessEffect />}
        {showEffect === 'fail' && <FailureEffect />}
      </div>

      {/* 間違い演出（遺体発見）。旧 overlay bg-black/95 / 遺体シルエット fill #334155 / メッセージ text-slate-400。
          opacity-20 / italic / blur(1px) / animation は非色のため JSX に残す */}
      {gameState === 'wrong_body' && (
        <div
          className="t15-wrong-overlay absolute inset-0 flex items-center justify-center"
          style={{ zIndex: 65, animation: 't15fadeIn 0.5s ease-out' }}
        >
          <div className="text-center p-8 max-w-md">
            <div className="mb-8 opacity-20">
              <svg viewBox="0 0 100 150" className="w-40 h-60 mx-auto" style={{ filter: 'blur(1px)' }}>
                <ellipse cx="50" cy="20" rx="15" ry="18" className="t15-corpse-shape" />
                <rect x="35" y="38" width="30" height="50" rx="5" className="t15-corpse-shape" />
                <rect x="25" y="40" width="10" height="35" rx="3" className="t15-corpse-shape" />
                <rect x="65" y="40" width="10" height="35" rx="3" className="t15-corpse-shape" />
                <rect x="38" y="88" width="10" height="40" rx="3" className="t15-corpse-shape" />
                <rect x="52" y="88" width="10" height="40" rx="3" className="t15-corpse-shape" />
              </svg>
            </div>
            <p
              className="t15-wrong-text text-xl italic"
              style={{ fontFamily: 'serif', animation: 't15fadeIn 1s ease-out 0.3s both' }}
            >
              {currentWrongMessage}
            </p>
          </div>
        </div>
      )}

      {/* 確認ダイアログ。旧 overlay bg-black/85 / panel bg-slate-950 border-slate-800 /
          doornum text-slate-300 / prompt text-slate-500 / cancel bg-slate-800 hover:bg-slate-700 text-slate-400 /
          open bg-slate-700 hover:bg-slate-600 text-slate-200。border 幅 / transition-colors / opacity-60 / animation は JSX に残す */}
      {gameState === 'confirming' && pendingDoor && (
        <div className="t15-confirm-overlay absolute inset-0 flex items-center justify-center" style={{ zIndex: 50 }}>
          <div
            className="t15-confirm-panel border rounded-lg p-8 max-w-sm mx-4"
            style={{ animation: 't15scaleIn 0.3s ease-out' }}
          >
            <div className="text-center mb-8">
              <div className="text-5xl mb-4 opacity-60">🚪</div>
              <h3 className="t15-confirm-doornum text-lg mb-3" style={{ fontFamily: 'serif' }}>
                {pendingDoor.door.label}番の扉
              </h3>
              <p className="t15-confirm-prompt text-sm">
                本当に開けるのか？
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleCancel}
                className="t15-confirm-cancel flex-1 px-4 py-3 rounded transition-colors text-sm"
              >
                やめる
              </button>
              <button
                onClick={handleConfirmOpen}
                className="t15-confirm-open flex-1 px-4 py-3 rounded transition-colors text-sm"
              >
                開ける
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ヘッダー（旧 chip bg-black/50 border-slate-800 / Clock text-slate-500 / AlertTriangle text-slate-600 /
          timer text-slate-400(<=10 で text-red-400) / 残り text-slate-500 / 数 text-slate-300(<=2 で text-red-400)）。
          AlertTriangle は icons namespace 不在のため同義名 TriangleAlert へ。border 幅 / font-mono / font-medium は JSX に残す */}
      <div className="absolute top-4 left-4 right-4 flex justify-between items-center" style={{ zIndex: 10 }}>
        {resolvedConfig.showTimer && resolvedConfig.timeLimitSec > 0 && (
          <div className="t15-chip flex items-center gap-2 px-3 py-1.5 rounded border">
            <Icon name="Clock" size={16} className="t15-chip-icon" />
            <span className={`font-mono text-sm ${timeLeft <= 10 ? 't15-timer--critical' : 't15-timer'}`}>
              {formatTime(timeLeft)}
            </span>
          </div>
        )}
        <div className="t15-chip flex items-center gap-2 px-3 py-1.5 rounded border">
          <Icon name="TriangleAlert" size={16} className="t15-chip-icon--dim" />
          <span className="t15-chip-text text-sm">
            残り <span className={`font-medium ${remainingAttempts <= 2 ? 't15-attempts--critical' : 't15-attempts'}`}>
              {remainingAttempts}
            </span>
          </span>
        </div>
      </div>

      {/* タイトル */}
      <div className="text-center mb-8 mt-16 px-4" style={{ zIndex: 10 }}>
        {/* タイトル群（旧 title text-slate-400 / instruction text-slate-600 / mistake count text-slate-600）。serif/letterSpacing は JSX に残す */}
        {resolvedConfig.title && (
          <h2 className="t15-title text-xl mb-2" style={{ fontFamily: 'serif', letterSpacing: '0.1em' }}>
            {resolvedConfig.title}
          </h2>
        )}
        <p className="t15-instruction text-sm">{resolvedConfig.instruction}</p>
        {mistakes > 0 && (
          <p className="t15-mistake-count text-xs mt-3" style={{ fontFamily: 'serif' }}>
            {mistakes}人の遺体を確認した...
          </p>
        )}
      </div>

      {/* 扉グリッド */}
      <div
        className="grid grid-cols-5 gap-2 sm:gap-3 max-w-xl px-4 transition-all duration-700"
        style={{
          zIndex: 10,
          transform: zoomingIn ? 'scale(3) translateY(20%)' : 'scale(1)',
          opacity: zoomingIn ? 0 : 1
        }}
      >
        {resolvedConfig.doors.map((door, index) => {
          const isTried = triedDoors.has(door.id);
          const isOpening = openingDoorIndex === index;
          const canInteract = gameState === 'playing' && !isTried;

          return (
            <div
              key={door.id}
              className="relative"
              style={{ perspective: '800px' }}
            >
              {/* 扉ボタン（canInteract/disabled の色のみ .t15-door--* に抽出。
                  旧 interactive: bg-slate-900/90 border-slate-700 hover:border-slate-500 hover:bg-slate-800/90 /
                     locked: bg-slate-950/80 border-slate-800。
                  cursor-pointer / opacity-20 / border 幅 / transform / boxShadow は非色のため JSX に残す） */}
              <button
                onClick={() => handleDoorClick(door, index)}
                disabled={!canInteract}
                className={`
                  relative w-16 h-24 sm:w-20 sm:h-28 rounded transition-all duration-300
                  flex flex-col items-center justify-center border
                  ${isTried ? 'opacity-20' : ''}
                  ${canInteract
                    ? 't15-door--interactive cursor-pointer'
                    : 't15-door--locked'}
                `}
                style={{
                  transformOrigin: 'left center',
                  transform: isOpening ? 'rotateY(-85deg)' : 'rotateY(0deg)',
                  transition: 'transform 0.8s ease-in-out',
                  boxShadow: canInteract ? '0 4px 20px rgba(0,0,0,0.4)' : 'none'
                }}
              >
                {/* 扉デザイン */}
                <div className="w-12 h-18 sm:w-14 sm:h-20 flex flex-col items-center justify-center">
                  {/* 上部装飾（旧 bg-slate-700/50） */}
                  <div className="t15-door-deco w-10 h-1 rounded mb-2" />

                  {/* 番号（旧 tried=text-slate-700 / else text-slate-500）。font-light は JSX に残す */}
                  <span className={`text-xl sm:text-2xl font-light ${isTried ? 't15-door-num--tried' : 't15-door-num'}`}>
                    {door.label}
                  </span>

                  {/* 下部装飾（旧 bg-slate-700/50） */}
                  <div className="t15-door-deco w-10 h-1 rounded mt-2" />
                </div>

                {/* ドアノブ（旧 tried=bg-slate-800 / else bg-slate-600）。-translate-y-1/2 は非色のため JSX に残す */}
                <div
                  className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-1.5 h-3 rounded-full ${isTried ? 't15-doorknob--tried' : 't15-doorknob'}`}
                />

                {/* 開いた扉の裏側（暗闇。旧 bg-black）。transform / backfaceVisibility は JSX に残す */}
                {isOpening && (
                  <div
                    className="t15-door-back absolute inset-0 rounded"
                    style={{
                      transform: 'rotateY(180deg)',
                      backfaceVisibility: 'hidden'
                    }}
                  />
                )}
              </button>

              {/* 扉の向こう側の暗闇（旧 bg-black）。-z-10 / animation は JSX に残す */}
              {isOpening && (
                <div
                  className="t15-door-void absolute inset-0 rounded -z-10"
                  style={{
                    animation: 't15doorVoid 0.8s ease-out forwards'
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* 結果メッセージ（成功/失敗とも瞬間結果は SuccessEffect/FailureEffect の全画面演出 + 自動 submit で進む。
          ボタン付きモーダルではないため JudgementModal に集約せず、静かなフェード文言を .t15-* で温存して色のみトークン化。
          旧 success text-slate-400 / fail text-red-400/80。serif / animation は JSX に残す） */}
      {gameState === 'success' && (
        <div className="mt-8 text-center" style={{ zIndex: 10, animation: 't15fadeIn 0.5s ease-out' }}>
          <p className="t15-success-text text-lg" style={{ fontFamily: 'serif' }}>
            {resolvedConfig.successMessage}
          </p>
        </div>
      )}
      {gameState === 'fail' && (
        <div className="mt-8 text-center" style={{ zIndex: 10, animation: 't15fadeIn 0.5s ease-out' }}>
          <p className="t15-fail-text text-lg" style={{ fontFamily: 'serif' }}>
            {resolvedConfig.timeLimitSec > 0 && timeLeft <= 0 ? '時間切れ...' : resolvedConfig.failureMessage}
          </p>
        </div>
      )}

      {/* 心拍インジケーター（旧 text-slate-600）。animation は JSX に残す */}
      {resolvedConfig.heartbeatEnabled && gameState !== 'success' && gameState !== 'fail' && (
        <div className="t15-heartbeat absolute bottom-4 left-4 flex items-center gap-2" style={{ zIndex: 10 }}>
          <span style={{ animation: `t15heartbeat ${heartbeatSpeed}ms ease-in-out infinite` }}>
            ♥
          </span>
        </div>
      )}

      <style>{`
        @keyframes t15fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes t15scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes t15doorVoid {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes t15heartbeat {
          0%, 100% { transform: scale(1); opacity: 0.4; }
          15% { transform: scale(1.2); opacity: 0.7; }
          30% { transform: scale(1); opacity: 0.4; }
          45% { transform: scale(1.15); opacity: 0.6; }
          60% { transform: scale(1); opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default Template15DoorSelection;
